import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	SearchBar,
	PageHeader,
	SelectInput,
} from "frame/components";
import OrganisationListing from "./organisation-listing";
import AccountSummary from "./account-summary";
import EditOrganisationForm from "../edit-organisation-form-container";
import { Typography } from "@material-ui/core";
import { isSuperAdminUser, useCurrentRole, useIsOnline } from "utils";
import { filterAccountsBySummary } from "../../utils/account-summary";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: "transparent",
		padding: (props) => (props.isAccountPage ? 0 : undefined),
		borderRadius: (props) => (props.isAccountPage ? 0 : undefined),
		boxShadow: (props) => (props.isAccountPage ? "none" : undefined),
	},
	heading: {
		padding: theme.spacing(3),
	},
	body: {
		marginTop: (props) => (props.isAccountPage ? theme.spacing(1) : theme.spacing(3)),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	filter: { display: "flex" },
	searchNoPad: {
		paddingLeft: 0,
	},
	filterOptions: {
		position: "relative",
		right: 25,
		top: 10,
		marginTop: theme.spacing(0.1),
		"& .MuiOutlinedInput-input": {
			padding: theme.spacing(1.7, 2),
		},
		"& .MuiInputLabel-outlined": {
			transform: "translate(14px, 14px) scale(1)",
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)",
		},
		[theme.breakpoints.down("sm")]: {
			right: -100,
		},
		[theme.breakpoints.down("xs")]: {
			top: 50,
			left: 20,
			minWidth: "100px",
		},
		minWidth: "150px",
	},
	offlineMessage: {
		color: theme.palette.error.main,
		marginTop: theme.spacing(-2),
		paddingBottom: theme.spacing(1),
	},
}));

const OrganisationList = ({
	flag,
	organisations,
	filter,
	onEdit,
	onDelete,
	onSearchChanged,
	onFetchOrganisations,
	onChangeOrganisation,
	onStatusFilterChange,
	onActivateDeactivate,
}) => {
	const theme = useTheme();
	const mobileView = useMediaQuery(theme.breakpoints.down("xs"));
	const currentRole = useCurrentRole();
	const isSuperAdmin = isSuperAdminUser(currentRole);

	const isAccountPage = window.location.pathname.includes("/account");
	const classes = useStyles({ isAccountPage });

	const isOnline = useIsOnline();
	const [summaryFilter, setSummaryFilter] = useState(null);
	const visibleOrganisations = useMemo(
		() => (isSuperAdmin ? filterAccountsBySummary(organisations, summaryFilter) : organisations),
		[isSuperAdmin, organisations, summaryFilter]
	);

	useEffect(() => {
		onStatusFilterChange("Active");
	}, [onStatusFilterChange]);

	useEffect(() => {
		onFetchOrganisations();
	}, [onFetchOrganisations]);

	// const fetchSuccess = flag.done && flag.success

	const onFilterChange = (value) => onStatusFilterChange(value);

	return (
		<Page className={classes.root} title="Accounts">
			<ScrollToTopOnMount />

			{!mobileView && !isAccountPage && <PageHeader title="Accounts" />}

			{isSuperAdmin && (
				<AccountSummary
					organisations={organisations}
					selected={summaryFilter}
					onSelect={setSummaryFilter}
				/>
			)}

			<div className={classes.filter}>
				<SearchBar className={classes.searchNoPad} onSearch={onSearchChanged} />

				<div className={classes.filterOptions}>
					<SelectInput
						noNone
						id="filter-options"
						label="Filter"
						options={["All", "Active", "Inactive"]}
						value={filter}
						onChange={onFilterChange}
					/>
				</div>
			</div>

			<PageContent padBottom>
				<p style={{ marginTop: 5 }}>
					{organisations && organisations.length > 0
						? visibleOrganisations.length + " records"
						: "No records loaded"}
				</p>
				<div className={classes.body}>
					{!isOnline && (
						<Typography
							className={classes.offlineMessage}
							variant="body1"
							align="right"
						>
							Please connect to internet to switch organisations
						</Typography>
					)}

					{visibleOrganisations &&
						visibleOrganisations.map((data, index) => (
							<OrganisationListing
								key={index}
								organisation={data}
								onEdit={onEdit}
								onDelete={onDelete}
								onChangeOrganisation={onChangeOrganisation}
								onActivateDeactivate={onActivateDeactivate}
							/>
						))}
				</div>
			</PageContent>

			<EditOrganisationForm />
		</Page>
	);
};

OrganisationList.propTypes = {
	flag: PropTypes.object.isRequired,
	filter: PropTypes.string.isRequired,
	organisations: PropTypes.array.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSearchChanged: PropTypes.func.isRequired,
	onFetchOrganisations: PropTypes.func.isRequired,
	onChangeOrganisation: PropTypes.func.isRequired,
	onStatusFilterChange: PropTypes.func.isRequired,
	onActivateDeactivate: PropTypes.func,
};

export default OrganisationList;
